import Big from 'big.js';
import axios from 'smg/axios';
import * as smgCarts from 'smg/customer-api/carts';
import { GetChildren, GetLineItems, GetOptions, GetPrices, GetSizes, GetTypes } from 'smg/customer-api/lineitems';
import { HandleRequestError } from 'smg/errors';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import * as models from 'smg/models';
import { Clone } from 'smg/template';
import * as toast from 'smg/toast';
import { DollarFormat } from 'smg/util';
import { UUID } from 'smg/uuid';
import { PromptProofForRevision, QuoteProofForRevision } from './components/revisions';
import { Relref } from './hugo';
import { PromptQuantityChange } from './quote';

let cartPrompted: boolean = false;

export const GetCart = smgCarts.GetCart;
export const GetCartID = smgCarts.GetCartID;
export const GetCartCount = smgCarts.GetCartCount;
export const DeleteCart = smgCarts.DeleteCart;
export const SetCartID = smgCarts.SetCartID;
export type CartResponse = smgCarts.CartResponse;
export type TaxAddress = smgCarts.TaxAddress;

interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

export declare interface AddCartItemOpts {
    promptToCheckout?: boolean;
    hideCartLink?: boolean;
    showToast?: boolean;
    newCart?: boolean;
}

const defaultAddCartItemOpts = {
    promptToCheckout: true,
    hideCartLink: false,
    showToast: true,
    newCart: false,
};

export async function AddCartItem(cartItems: smgCarts.AddCartItemParams | smgCarts.AddCartItemParams[], opts: AddCartItemOpts = defaultAddCartItemOpts) {
    const promptToCheckout = opts.promptToCheckout ?? defaultAddCartItemOpts.promptToCheckout;
    const hideCartLink = opts.hideCartLink ?? defaultAddCartItemOpts.hideCartLink;
    const showToast = opts.showToast ?? defaultAddCartItemOpts.showToast;
    const newCart = opts.newCart ?? defaultAddCartItemOpts.newCart;

    return await smgCarts.AddCartItems(cartItems, newCart).
        then(resp => {
            if (showToast) {
                toast.Success(`<div>Added to cart! ${hideCartLink ? '' : `<a href='${Relref('/cart')}' class="text-reset text-decoration-underline">Go to cart</a></div>`}`);
            }

            if (promptToCheckout && !cartPrompted) {
                new Modal({
                    body: `
                    <div class="d-flex flex-column align-items-center text-center">
                        <div class="icon-mark icon-mark-lg mb-6">
                            <span class="material-symbols-rounded">task_alt</span>
                        </div>
                        <h2 class="fw-900 mb-2">Added to cart!</h2>
                        <p class="mb-0">
                            Your quote has been added to your cart. Ready to checkout?
                        </p>
                    </div>`,
                    cancelText: 'Keep Shopping',
                    confirmText: 'Go to Cart',
                    onSubmit: () => {
                        window.location.href = Relref('/cart');
                    },
                    appendConfirmButtonClass: 'w-100 w-sm-auto btn-sm-lg',
                    cancelButtonClass: 'btn w-100 w-sm-auto btn-sm-lg btn-outline-primary',
                    appendClassBySelector: {
                        '.modal-dialog': 'proof-request-sent-modal-dialog px-4 px-6',
                        '.modal-body': 'px-6 pt-8 pb-2',
                        '.modal-footer': 'p-6 pt-sm-10 pb-sm-14 justify-content-center',
                    },
                });

                cartPrompted = true;
            }

            if (newCart) {
                ResetCartCount();
            }

            SetCartCount(resp.cartItemsCount);
        }).catch(HandleRequestError);
}

export async function DeleteCartItem(cartItemID: string, quantity: number = 1, onUndo?: () => void) {
    return axios.delete(`/cart-items/${cartItemID}`).
        then(() => {
            const undoLinkID = `undo-${UUID()}`;
            const t = toast.Success(`<div>Removed from cart. <a href="${Relref('/cart')}" class="text-reset text-decoration-underline" id="${undoLinkID}">Undo</a></div>`);

            const undo = document.querySelector(`#${undoLinkID}`) as HTMLAnchorElement;
            undo.addEventListener('click', async e => {
                e.preventDefault();

                await smgCarts.RestoreCartItem(cartItemID).
                    then(() => IncrementCartCount(quantity)).
                    then(() => onUndo?.());

                t?.dispose();
            });
            DecrementCartCount(quantity);
        });
}

let cartCount: number = 0;
let navCartLink: HTMLElement | undefined;
let navCartCount: NodeListOf<HTMLElement> | undefined;
let navCartIcon: HTMLElement | undefined;

function initCarNavLinks() {
    if (!navCartLink || !navCartCount || !navCartIcon) {
        navCartLink = document.querySelector('#nav-cart-link') as HTMLElement;
        navCartCount = document.querySelectorAll('.nav-cart-count');
        navCartIcon = document.querySelector('#shopping-cart-icon') as HTMLElement;
    }
}

export function SetCartCount(qty: number) {
    initCarNavLinks();

    navCartIcon?.classList.remove('opacity-75');
    if (navCartCount) {
        navCartCount.forEach(el => {
            el.classList.remove('d-none');
            el.textContent = String(qty);
        });
    }
}

export function IncrementCartCount(add: number = 1) {
    initCarNavLinks();

    cartCount += add;

    navCartIcon?.classList.remove('opacity-75');
    if (navCartCount) {
        navCartCount.forEach(el => {
            el.classList.remove('d-none');
            el.textContent = String(cartCount);
        });
    }
}

export function DecrementCartCount(minus: number = 1) {
    initCarNavLinks();

    cartCount -= minus;
    cartCount = Math.max(cartCount, 0);

    navCartIcon?.classList.add('opacity-75');
    if (navCartCount) {
        navCartCount.forEach(el => {
            if (cartCount < 1) {
                el.classList.add('d-none');
                return;
            }
            el.classList.remove('d-none');
            el.textContent = String(cartCount);
        });
    }
}

export function ResetCartCount() {
    DecrementCartCount(cartCount);
}

export function GetCurrentCartCount() {
    return cartCount;
}

export async function PromptQuantityEdit(defaultQty: number, quantityPricesObj: Record<string, string>): Promise<number> {
    const body = Clone('#cart-item-quantity-editor-template').firstElementChild as HTMLElement;

    const quantityPrices = Object.keys(quantityPricesObj).map(q => ({ qty: parseInt(q, 10), price: quantityPricesObj[q] })).sort((a, b) => a.qty - b.qty);

    const optionsParent = body.querySelector<HTMLElement>('.qty-editor-options-parent');

    let selectedQty = false;

    // find the price from the quantity prices thats the last quantity before the given qty
    const getPriceFromQty = (qty: number) => {
        for (let i = quantityPrices.length - 1; i >= 0; i--) {
            if (quantityPrices[i].qty <= qty) {
                return quantityPrices[i].price;
            }
        }
    };

    const customQtyInputContainer = body.querySelector<HTMLElement>('.qty-editor-custom-input-container');
    const customQtyInput = body.querySelector<HTMLInputElement>('.qty-editor-custom-input');
    const customQtyTotal = customQtyInputContainer?.querySelector<HTMLElement>('.qty-editor-total');

    const drawCustomTotals = () => {
        if (customQtyInputContainer) {
            const qty = parseInt(customQtyInput?.value || '0', 10);
            const price = getPriceFromQty(qty);

            if (customQtyTotal) {
                customQtyTotal.innerText = DollarFormat(Big(price || 0).times(qty));
            }

            if (price) {
                drawSavings(customQtyInputContainer, qty, Big(price));
            } else {
                drawSavings(customQtyInputContainer);
            }
        }
    };

    const showCustom = (show: boolean) => {
        customQtyInputContainer?.classList.toggle('d-none', !show);
        if (customQtyInput) {
            customQtyInput.disabled = !show;
        }

        drawCustomTotals();

        setTimeout(() => {
            customQtyInput?.focus();
            customQtyInput?.select();
        });
    };

    if (customQtyInput) {
        customQtyInput.value = String(defaultQty);
    }

    const drawSavings = (el: HTMLElement, qty?: number, price?: Big) => {
        const saveContainer = el.querySelector<HTMLElement>('.qty-editor-save-container');
        const savePercent = saveContainer?.querySelector<HTMLElement>('.qty-editor-save-percent');

        if (!saveContainer || !savePercent) {
            return;
        }

        saveContainer.classList.add('d-none');

        if (qty && price) {
            const totalWithoutDiscount = Big(quantityPrices[0].price).times(qty);
            const total = price.times(qty);
            const savings = totalWithoutDiscount.minus(total);
            const save = savings.div(totalWithoutDiscount);
            savePercent.textContent = Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 0 }).format(save.toNumber());

            if (saveContainer && save.gt(0)) {
                saveContainer.classList.remove('d-none');
            }
        }
    };

    if (quantityPrices.length <= 1) {
        optionsParent?.classList.add('d-none');
        for (const qp of quantityPrices) {
            if (customQtyInput) {
                customQtyInput.min = String(qp.qty);
            }
        }
    } else {
        for (const qp of quantityPrices) {
            if (customQtyInput && !customQtyInput.min) {
                customQtyInput.min = String(qp.qty);
            }

            const el = Clone('.cart-item-quantity-editor-qty-template', body).firstElementChild as HTMLElement;
            const qty = el.querySelector<HTMLElement>('.qty-editor-qty');
            const price = el.querySelector<HTMLElement>('.qty-editor-price');
            const total = el.querySelector<HTMLElement>('.qty-editor-total');

            if (qty) {
                qty.textContent = Intl.NumberFormat().format(qp.qty);
            }

            if (price) {
                price.textContent = DollarFormat(qp.price);
            }

            if (total) {
                total.textContent = DollarFormat(qp.qty * parseFloat(qp.price));
            }

            drawSavings(el, qp.qty, Big(qp.price));

            const radio = el.querySelector('input') as HTMLInputElement;
            radio.value = String(qp.qty);

            if (defaultQty === qp.qty) {
                radio.checked = true;
                selectedQty = true;

                if (customQtyInput) {
                    customQtyInput.value = String(qp.qty);
                }

                showCustom(false);
            }

            radio.addEventListener('click', () => {
                showCustom(false);
                if (customQtyInput) {
                    customQtyInput.value = String(qp.qty);
                    customQtyInput.dispatchEvent(new Event('input'));
                }
            });

            optionsParent?.appendChild(el);
        }
    }

    const customOption = body.querySelector<HTMLElement>('.qty-editor-custom-option');
    if (customOption) {
        optionsParent?.appendChild(customOption);
        const radio = customOption?.querySelector('input') as HTMLInputElement;

        if (!selectedQty) {
            radio.checked = true;
            showCustom(true);
        }

        radio.addEventListener('click', () => {
            showCustom(true);
        });

        customQtyInput?.addEventListener('input', () => drawCustomTotals());
    }

    return new Promise((resolve, reject) => {
        new Modal({
            body,
            title: 'Edit Quantity',
            confirmText: 'Save',
            onSubmit: () => {
                const checked = body.querySelector<HTMLInputElement>('input:checked');
                if (checked?.value === 'custom') {
                    resolve(parseInt(customQtyInput?.value || '0', 10));
                } else {
                    resolve(parseInt(checked?.value || '0', 10));
                }
            },
            onClose: () => reject(new Error('Modal closed')),
        });

        showCustom(customQtyInputContainer?.classList.contains('d-none') === false);
    });
}

export interface DrawQuoteOrOrderCartItemOptions {
    cartItem: models.CartItem;
    deleteSuccess?: () => void;
    restoreSuccess?: () => void;
    disableDelete?: boolean;
    addToCart?: boolean;
}

export async function DrawQuoteOrOrderCartItem(opts: DrawQuoteOrOrderCartItemOptions): Promise<HTMLElement | null> {
    if (!opts.cartItem.quote && !opts.cartItem.order) {
        return null;
    }

    const usedProofs = new Set<string>();

    // the cart API returns all this info already,
    // but the quote search page does not, so we need to get all the line item,
    // types, sizes, etc. names

    if (opts.cartItem.quote?.quoteLineItems) {
        const quoteProofs = new Map<string, models.QuoteProof>();
        if (opts.cartItem.quote.quoteProofs) {
            for (const quoteProof of opts.cartItem.quote.quoteProofs) {
                quoteProofs.set(quoteProof.id ?? '', quoteProof);
            }
        }

        for (const quoteLineItem of opts.cartItem.quote.quoteLineItems) {
            if (quoteLineItem.quoteProofID && !quoteLineItem.quoteProof) {
                quoteLineItem.quoteProof = quoteProofs.get(quoteLineItem.quoteProofID);
            }

            if (quoteLineItem.lineItemID && !quoteLineItem.lineItemModel?.name) {
                const lineItems = await GetLineItems();
                const lineItem = lineItems.find(l => l.id === quoteLineItem.lineItemID);
                quoteLineItem.lineItemModel = { ...quoteLineItem.lineItemModel, name: lineItem?.name };
            }

            if (quoteLineItem.lineItemTypeID && !quoteLineItem.lineItemType?.name && quoteLineItem.lineItemModel?.name) {
                const types = await GetTypes(quoteLineItem.lineItemModel.name);
                const type = types.find(t => t.id === quoteLineItem.lineItemTypeID);
                quoteLineItem.lineItemType = { ...quoteLineItem.lineItemType, name: type?.name };
            }

            if (quoteLineItem.lineItemTypeSizeID && !quoteLineItem.lineItemTypeSize?.name && quoteLineItem.lineItemModel?.name && quoteLineItem.lineItemType?.name) {
                const sizes = await GetSizes(quoteLineItem.lineItemModel.name, quoteLineItem.lineItemType.name);
                const size = sizes.find(s => s.id === quoteLineItem.lineItemTypeSizeID);
                quoteLineItem.lineItemTypeSize = { ...quoteLineItem.lineItemTypeSize, name: size?.name };
            }

            if (quoteLineItem.lineItemTypeSizeID && !quoteLineItem.lineItemTypeSize?.networkLineItemTypeSize?.prices &&
                quoteLineItem.lineItemModel?.name && quoteLineItem.lineItemType?.name && quoteLineItem.lineItemTypeSize?.name) {
                const prices: models.NetworkLineItemTypeSizeQuantityPrice[] = [];

                const pricesRes = await GetPrices(quoteLineItem.lineItemModel.name, quoteLineItem.lineItemType?.name, 0, 0, '', quoteLineItem.lineItemTypeSize.name);
                for (const price of pricesRes) {
                    prices.push({
                        quantity: price.quantity,
                        price: price.price.toString(),
                    });
                }

                quoteLineItem.lineItemTypeSize.networkLineItemTypeSize = { ...quoteLineItem.lineItemTypeSize.networkLineItemTypeSize, prices };
            }

            if (quoteLineItem.quoteChildLineItems) {
                for (const quoteChildLineItem of quoteLineItem.quoteChildLineItems) {
                    if (quoteChildLineItem.childLineItemID && !quoteChildLineItem.childLineItem?.name && quoteLineItem.lineItemModel?.name) {
                        const childLineItems = await GetChildren(quoteLineItem.lineItemModel.name);
                        const child = childLineItems.find(c => c.id === quoteChildLineItem.childLineItemID);
                        quoteChildLineItem.childLineItem = { ...quoteChildLineItem.childLineItem, name: child?.name };
                    }

                    if (quoteChildLineItem.childLineItemOptionID && !quoteChildLineItem.childLineItemOption?.name && quoteLineItem.lineItemModel?.name && quoteChildLineItem.childLineItem?.name) {
                        const childLineItemOptions = await GetOptions(quoteLineItem.lineItemModel.name, quoteChildLineItem.childLineItem.name);
                        const option = childLineItemOptions.find(o => o.id === quoteChildLineItem.childLineItemOptionID);
                        quoteChildLineItem.childLineItemOption = { ...quoteChildLineItem.childLineItemOption, name: option?.name };
                    }
                }
            }
        }
    }

    const childLineItemDiv = (t1?: string, t2?: string, desc?: string, price?: string) => {
        if (!t1 && !t2 && !desc && !price) {
            return '';
        }

        let h = '';
        if (t1) {
            h += `<span class="fw-semibold">${html.Escape(t1 ?? '')}</span>`;
        }
        if (h && t2) {
            h += ': ';
        }
        if (t2) {
            h += html.Escape(t2);
        }
        if (h && desc) {
            h += ` (${html.Escape(desc)})`;
        } else if (desc) {
            h = html.Escape(desc);
        }

        return `
        <div class="cart-child-line-item-detail d-flex flex-wrap">
            <span>${h}</span>
            <span class="fst-italic ms-auto unit-price">${html.Escape(price ?? '')}</span>
        </div>`;
    };

    const selectedOptions = (li: models.QuoteLineItem | models.OrderLineItem) => {
        let out = '';

        let unitName = 'unit';

        switch (li.lineItemModel?.name) {
        case 'Lapel Pin':
            unitName = 'pin';
            break;
        case 'Belt Buckle':
            unitName = 'buckle';
            break;
        case 'Challenge Coin':
            unitName = 'coin';
            break;
        case 'Key Chain':
        case 'Keychain':
            unitName = 'keychain';
            break;
        case 'Lanyard':
            unitName = 'lanyard';
            break;
        case 'Medal':
            unitName = 'medal';
            break;
        case 'Patch':
            unitName = 'patch';
            break;
        case 'Wrist Band':
            unitName = 'wristband';
            break;
        case 'Ornament':
            unitName = 'ornament';
            break;
        default:
            unitName = 'unit';
            break;
        }

        out += childLineItemDiv('Type', li.lineItemType?.name || li.type, li.description);
        if (!Big(li.price || 0).eq(0)) {
            out += childLineItemDiv('Size', li.lineItemTypeSize?.name || li.size, undefined, `${DollarFormat(li.price ?? 0)} per ${unitName}`);
        } else {
            out += childLineItemDiv('Size', li.lineItemTypeSize?.name || li.size);
        }

        let childLineItems: (models.QuoteChildLineItem | models.OrderChildLineItem)[] = [];
        if ('quoteChildLineItems' in li) {
            childLineItems = li.quoteChildLineItems || [];
        } else if ('orderChildLineItems' in li) {
            childLineItems = li.orderChildLineItems || [];
        }
        for (const childLineItem of childLineItems) {
            let child: string | undefined;
            let option: string | undefined;
            let price: string | undefined;
            if (childLineItem.childLineItem?.networkChildLineItem?.enabled || childLineItem.child || childLineItem.description || !Big(childLineItem.price || 0).eq(0)) {
                child = childLineItem.childLineItem?.name || childLineItem.child;
                option = childLineItem.childLineItemOption?.name || childLineItem.option;

                if (childLineItem.price && childLineItem.price !== '0') {
                    price = '+' + DollarFormat(Big(childLineItem.price));
                    if (childLineItem.quantity === li.quantity) {
                        price += ` per ${unitName}`;
                    } else if (childLineItem.quantity && childLineItem.quantity !== 1) {
                        price += ` x${childLineItem.quantity.toLocaleString() ?? ''}`;
                    }
                } else {
                    price = 'Included';
                }
                out += childLineItemDiv(child, option, childLineItem.description, price);
            }
        }
        return out;
    };

    const drawLineItem = (li: models.QuoteLineItem | models.OrderLineItem) => {
        const uuid = UUID();
        let proof: models.Proof | undefined;
        let proofName: string | undefined;
        if ('quoteProof' in li) {
            proof = li.quoteProof?.proof;
            proofName = li.quoteProof?.name;
            usedProofs.add(proof?.file ?? '');
        } else if ('orderProof' in li) {
            proof = li.orderProof?.proof;
            proofName = li.orderProof?.name;
            usedProofs.add(proof?.file ?? '');
        }
        let $proof = '';
        if (proof?.file) {
            $proof = `
            <div class="sample-image">
                <a href="${window.cloudFront}/${proof.file}" data-fancybox="${uuid}" data-caption="${html.Escape(proofName ?? '')}">
                    <img class="w-100" src="${window.cloudFront}/${proof.file}" alt="${html.Escape(proofName ?? '')}" />
                </a>
            </div>`;
        }

        return `
        <div id="cart-item-${uuid}" class="cart-item-card-body pb-14 d-flex flex-wrap gap-6">
            ${$proof}
            <div class="cart-item-details d-flex flex-column gap-1 gap-sm-4">
                ${proofName ? `<div class="cart-item-title fs-5 fw-medium">${html.Escape(proofName)}</div>` : ''}
                <div>
                    <div class="cart-item-quantity-line mb-1"><b>Quantity:</b> <span class="cart-item-quantity">${Intl.NumberFormat().format(li.quantity ?? 0)}</span></div>
                    <div class="cart-item-product-line"><b>Product:</b> <span class="cart-item-product">${html.Escape(li.lineItem || li.lineItemModel?.name || '')}</span></div>
                </div>
                <div class="cart-item-controls d-none d-md-block">
                    <div class="mb-4">
                        <button type="button" class="btn btn-outline-primary request-quantity-change-btn" data-li-id="${li.id ?? ''}"
                            data-qo-id="${opts.cartItem.quoteID ?? opts.cartItem.quote?.id ?? opts.cartItem.orderID ?? opts.cartItem.order?.id ?? ''}">
                            Request Quantity Change
                        </button>${proof?.file ? '<button type="button" class="btn btn-outline-primary ms-4 view-proof-btn desktop">View Proof</button>' : ''}
                    </div>
                    <div class="collapsible-container d-flex flex-column p-3">
                        <button id="selected-options-toggler-${uuid}-desktop" type="button"
                            class="btn p-0 m-0 ps-3 flex-grow-1 selected-options-toggler justify-content-start"
                            data-bs-toggle="collapse" data-bs-target="#selected-options-${uuid}-desktop">Selected Options</button>
                        <div id="selected-options-${uuid}-desktop" class="collapse show">
                            <div class="selected-options my-3">
                                ${selectedOptions(li)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="cart-item-controls-mobile w-100 d-md-none">
                <div class="d-flex flex-column gap-3">
                    <button type="button" class="btn btn-outline-primary request-quantity-change-btn mobile" data-li-id="${li.id ?? ''}"
                        data-qo-id="${opts.cartItem.quoteID ?? opts.cartItem.quote?.id ?? opts.cartItem.orderID ?? opts.cartItem.order?.id ?? ''}">Request Quantity Change</button>
                    ${proof?.file ? '<button type="button" class="btn btn-outline-primary view-proof-btn mobile">View Proof</button>' : ''}
                </div>
                <div class="collapsible-container w-auto flex-grow-1 d-flex flex-column p-3 mt-8">
                    <button id="selected-options-toggler-${uuid}-mobile" type="button" class="btn p-0 m-0 ps-3 flex-grow-1 selected-options-toggler justify-content-start"
                        data-bs-toggle="collapse" data-bs-target="#selected-options-${uuid}-mobile">Selected Options</button>
                    <div id="selected-options-${uuid}-mobile" class="collapse show">
                        <div class="selected-options my-3">
                            ${selectedOptions(li)}
                        </div>
                    </div>
                </div>
            </div>
        </div>`;
    };

    const drawLineItems = () => {
        let lineItems: (models.QuoteLineItem | models.OrderLineItem)[] = [];
        if (opts.cartItem.quote) {
            lineItems = opts.cartItem.quote.quoteLineItems || [];
        } else if (opts.cartItem.order) {
            lineItems = opts.cartItem.order.orderLineItems || [];
        }

        let out = '';
        for (const li of lineItems) {
            out += drawLineItem(li);
        }

        return out;
    };

    const drawAttachments = () => {
        const proofs: models.Proof[] = [];
        if (opts.cartItem.quote) {
            proofs.push(...opts.cartItem.quote.quoteProofs?.map(qp => qp.proof).filter(p => p !== undefined) || []);
        } else if (opts.cartItem.order) {
            proofs.push(...opts.cartItem.order.orderProofs?.map(op => op.proof).filter(p => p !== undefined) || []);
        }

        const uuid = UUID();
        const remaining = proofs.filter(p => p.file && !usedProofs.has(p.file));
        let $attachments = '';
        if (remaining.length > 0) {
            proofs.forEach(proof => {
                if (proof?.file && !usedProofs.has(proof.file)) {
                    $attachments += `
                    <div class="cart-item-attachment">
                        <a href="${window.cloudFront}/${proof.file}" data-fancybox="${uuid}" data-caption="${html.Escape(proof.name ?? '')}">
                            <img class="w-100" src="${window.cloudFront}/${proof.file}" alt="${html.Escape(proof.name ?? '')}" />
                        </a>
                    </div>`;
                }
            });
            return `<div class="cart-item-attachments-section d-flex flex-wrap gap-2"><h4 class="col-12 mb-2 fw-medium text-body">Attachments</h4>${$attachments}</div>`;
        } else {
            return '';
        }
    };

    let cartItemTitle = '';
    if (opts.cartItem.quote) {
        cartItemTitle = `Quote ${html.Escape(opts.cartItem.quote.serial ?? '')}`;
    } else if (opts.cartItem.order) {
        cartItemTitle = `Reorder ${opts.cartItem.order.invoiceNumber ?? ''}`;
    }
    const $card = html.FromString(`
        <div class="cart-item-card w-100 rounded rounded-sm-5 d-flex flex-column px-4 px-sm-8 px-md-12 py-8">
            <div class="cart-item-card-header d-flex flex-column flex-sm-row justify-content-between align-items-middle">
                <h3 class="align-self-top m-0 fw-800 fs-4 fs-sm-3">${cartItemTitle}</h3>
                <div class="text-end d-flex flex-sm-column justify-content-between align-items-center align-items-sm-end mb-8 mb-sm-0">
                    <div class="fs-4 fs-sm-3 fw-medium">${DollarFormat(opts.cartItem.subtotal || 0)}</div>
                    <button type="button" class="btn btn-sm btn-link price-breakdown-btn me-n2">View Price Breakdown</button>
                </div>
            </div>
            ${drawLineItems()}
            ${drawAttachments()}
            <div class="cart-item-footer d-none d-sm-flex justify-content-center justify-content-md-end">
                ${opts.addToCart ? '<button type="button" class="btn btn-link btn-lg cart-item-add-to-cart-btn"><span class="material-symbols-rounded">add_shopping_cart</span>Add to Cart</button>' : ''}
                ${opts.cartItem.quote ? '<button type="button" class="btn btn-link btn-lg cart-item-revision-btn"><span class="material-symbols-rounded">edit</span>Request Revision</button>' : ''}
                ${!opts.disableDelete ? '<button type="button" class="btn btn-link btn-lg cart-item-remove-btn"><span class="material-symbols-rounded">delete</span>Remove</button>' : ''}
            </div>
            <div class="cart-item-footer mobile d-flex d-sm-none justify-content-center">
                ${opts.addToCart ? '<button type="button" class="btn btn-link cart-item-add-to-cart-btn"><span class="material-symbols-rounded">add_shopping_cart</span>Add to Cart</button>' : ''}
                ${opts.cartItem.quote ? '<button type="button" class="btn btn-link cart-item-revision-btn"><span class="material-symbols-rounded">edit</span>Request Revision</button>' : ''}
                ${!opts.disableDelete ? '<button type="button" class="btn btn-link cart-item-remove-btn"><span class="material-symbols-rounded">delete</span>Remove</button>' : ''}
            </div>
        </div>`);

    $card.querySelectorAll<HTMLButtonElement>('.cart-item-remove-btn').forEach($btn => {
        $btn.addEventListener('click', async () => {
            if (opts.cartItem.id) {
                const cartItemID = opts.cartItem.id;
                const deleteFn = async () => {
                    await DeleteCartItem(cartItemID, 1, undoFn).then(opts.deleteSuccess);
                };
                const undoFn = () => {
                    const undoLinkID = `undo-${UUID()}`;
                    const t = toast.Success(`<div>Cart item restored. <a href="${Relref('/cart')}" class="text-reset text-decoration-underline" id="${undoLinkID}">Undo</a></div>`);

                    const undo = document.querySelector(`#${undoLinkID}`) as HTMLAnchorElement;
                    undo.addEventListener('click', e => {
                        e.preventDefault();
                        void deleteFn();
                        t?.dispose();
                    });
                    opts.restoreSuccess?.();
                };
                await deleteFn();
            }
        });
    });

    $card.querySelectorAll<HTMLElement>('.cart-item-card-body:has(a[data-fancybox]):has(.view-proof-btn)').forEach($el => {
        const $viewProofBtnDesktop = $el.querySelector('.view-proof-btn.desktop') as HTMLElement;
        const $viewProofBtnMobile = $el.querySelector('.view-proof-btn.mobile') as HTMLElement;
        const $proofFancyboxLink = $el.querySelector('a[data-fancybox]') as HTMLElement;
        $viewProofBtnDesktop.addEventListener('click', () => $proofFancyboxLink.click());
        $viewProofBtnMobile.addEventListener('click', () => $proofFancyboxLink.click());
    });

    $card.querySelectorAll<HTMLButtonElement>('.request-quantity-change-btn').forEach($el => {
        $el.addEventListener('click', async () => {
            const liID = $el.getAttribute('data-li-id');
            const qoID = $el.getAttribute('data-qo-id');
            if (liID && qoID) {
                const quote = opts.cartItem.quote;
                const order = opts.cartItem.order;
                const lineItems = (quote?.quoteLineItems || order?.orderLineItems)?.find(li => li.id === liID);
                if (lineItems) {
                    await PromptQuantityChange({ lineItem: lineItems, quote, order });
                }
            }
        });
    });

    $card.querySelectorAll<HTMLButtonElement>('.cart-item-add-to-cart-btn').forEach($el => {
        $el.addEventListener('click', async () => {
            const quote = opts.cartItem.quote;
            const order = opts.cartItem.order;
            if (quote) {
                await AddCartItem({ quoteID: quote.key ?? '' });
            } else if (order) {
                await AddCartItem({ orderID: order.key ?? '' });
            }
        });
    });

    $card.querySelectorAll<HTMLButtonElement>('.cart-item-revision-btn').forEach($el => {
        $el.addEventListener('click', async () => {
            const quote = opts.cartItem.quote;
            if (quote) {
                const quoteLineItemsByQuoteProofID = new Map<string, models.QuoteLineItem>();
                for (const quoteLineItem of quote.quoteLineItems || []) {
                    if (!quoteLineItem.quoteProofID) {
                        continue;
                    }
                    quoteLineItemsByQuoteProofID.set(quoteLineItem.quoteProofID, quoteLineItem);
                }

                const quoteProofs: QuoteProofForRevision[] = [];
                for (const quoteProof of quote.quoteProofs || []) {
                    quoteProofs.push({
                        quoteProof,
                        quoteLineItem: quoteLineItemsByQuoteProofID.get(quoteProof.id ?? ''),
                    });
                }

                const quoteProofID = await PromptProofForRevision(quoteProofs);
                if (!quoteProofID) {
                    return;
                }
                window.location.href = `${Relref('/revisions')}?id=${quote.key ?? ''}&proof=${quoteProofID}`;
            }
        });
    });

    interface tableLineItem {
        id: string;
        proof: models.Proof | undefined;
        description: string;
        quantity: number;
        pricePerUnit: Big;
        subtotal: Big;
        childLineItems: tableChildLineItem[] | undefined;
    }
    interface tableChildLineItem {
        id: string;
        child: string;
        option: string;
        description: string;
        quantity: number;
        pricePerUnit: Big;
        subtotal: Big;
    }

    const showPriceBreakdown = () => {
        let modalOpen = true;
        const $m = new Modal({
            title: 'Price Breakdown',
            headerLevel: 'h2',
            escape: false,
            body: drawCartItemBreakdownTable(),
            width: '57.5rem',
            cancelButtonClass: 'btn btn-primary mx-auto',
            cancelText: 'Close',
            appendBodyClass: 'py-0 px-4 px-sm-12 px-lg-20 overflow-auto',
            appendClassBySelector: { '.modal-title': 'fs-4 fs-sm-3 fs-md-2' },
            onClose: () => modalOpen = false,
            animation: true,
        });

        // Scroll shadows
        const $tableContainer = $m.Element.querySelector<HTMLDivElement>('.price-breakdown-container');
        const $table = $tableContainer?.querySelector<HTMLTableElement>('.price-breakdown');
        if (!$tableContainer || !$table) {
            return;
        }
        const scrollShadowLoop = () => {
            if (modalOpen) {
                setTimeout(scrollShadowLoop, 50);
            }
            const dTop = $tableContainer.scrollTop;
            const dBottom = $tableContainer.scrollHeight - $tableContainer.clientHeight - dTop;
            const dLeft = $tableContainer.scrollLeft;
            $table.style.setProperty('--smg-d-top', dTop.toString());
            $table.style.setProperty('--smg-d-bottom', dBottom.toString());
            $table.style.setProperty('--smg-d-left', dLeft.toString());
        };
        scrollShadowLoop();
    };

    const drawCartItemBreakdownTable = () => {
        let rows = '';

        const lineItems: tableLineItem[] = [];
        if (opts.cartItem.quote?.quoteLineItems) {
            opts.cartItem.quote.quoteLineItems.forEach(li => {
                lineItems.push({
                    id: li.id ?? '',
                    proof: li.quoteProof?.proof,
                    description: li.quoteProof?.name ?? li.lineItemModel?.name ?? '',
                    quantity: li.quantity ?? 0,
                    pricePerUnit: Big(li.price ?? 0),
                    subtotal: Big(li.price ?? 0).times(li.quantity ?? 1),
                    childLineItems: li.quoteChildLineItems?.map(qcli => ({
                        id: qcli.id ?? '',
                        child: qcli.childLineItem?.name || qcli.child || '',
                        option: qcli.childLineItemOption?.name || qcli.option || '',
                        description: qcli.description || '',
                        quantity: qcli.quantity ?? 0,
                        pricePerUnit: Big(qcli.price ?? 0),
                        subtotal: Big(qcli.price ?? 0).times(qcli.quantity ?? 1),
                    })),
                });
            });
        } else if (opts.cartItem.order?.orderLineItems) {
            opts.cartItem.order.orderLineItems.forEach(li => {
                lineItems.push({
                    id: li.id ?? '',
                    proof: li.orderProof?.proof,
                    description: li.orderProof?.name ?? '',
                    quantity: li.quantity ?? 0,
                    pricePerUnit: Big(li.price ?? 0),
                    subtotal: Big(li.price ?? 0),
                    childLineItems: li.orderChildLineItems?.map(ocli => ({
                        id: ocli.id ?? '',
                        child: ocli.childLineItem?.name || ocli.child || '',
                        option: ocli.childLineItemOption?.name || ocli.option || '',
                        description: ocli.description || '',
                        quantity: ocli.quantity ?? 0,
                        pricePerUnit: Big(ocli.price ?? 0),
                        subtotal: Big(ocli.price ?? 0).times(ocli.quantity ?? 1),
                    })),
                });
            });
        }

        const childLineItemName = (cli: tableChildLineItem) => {
            let h = '';

            if (cli.child && cli.option) {
                h += `<strong class="fw-semibold">${html.Escape(cli.child)}:</strong> ${html.Escape(cli.option)}`;
            } else if (cli.child) {
                h += `<strong class="fw-semibold">${html.Escape(cli.child)}</strong>`;
            } else if (cli.option) {
                h += `<strong class="fw-semibold">${html.Escape(cli.option)}</strong>`;
            }

            if (cli.description) {
                h += ` (${html.Escape(cli.description)})`;
            }

            return h;
        };

        const noChildLineItemsPlaceholder = `
        <tr>
            <td class="c2 py-2 fst-italic ps-10 text-start">No selected options</td>
            <td class="c3 py-2 fst-italic text-end">0</td>
            <td class="c4 py-2 fst-italic text-end">$0.00</td>
            <td class="c5 py-2 fst-italic text-end">$0.00</td>
        </tr>`;

        lineItems.forEach(item => {
            let $proof = '';
            if (item.proof?.file) {
                const uuid = UUID();
                $proof = `<a class="py-3 d-block position-sticky h-100" href="${window.cloudFront}/${item.proof.file}" data-fancybox="${uuid}" data-caption="${html.Escape(item.proof.name ?? '')}">
                    <img class="line-item-preview" src="${window.cloudFront}/${item.proof.file}" alt="${html.Escape(item.proof.name ?? '')}" />
                </a>`;
            }
            rows += `
                <tr>
                    <td class="c1 text-center py-0 px-4 position-sticky start-0 z-2" rowspan="2">${$proof}</td>
                    <td class="c2 text-start text-break">${html.Escape(item.description ?? '')}</td>
                    <td class="c3 text-end">${Intl.NumberFormat().format(item.quantity)}</td>
                    <td class="c4 text-end">${DollarFormat(item.pricePerUnit)}</td>
                    <td class="c5 text-end">${DollarFormat(item.subtotal)}</td>
                </tr>
                <tr>
                    <td class="selected-options-collapse p-0" colspan="4">
                        <a href="#children-${item.id}" data-bs-toggle="collapse" class="d-flex text-reset fw-bold small align-items-center py-2 px-4">
                            <span class="selected-options-sticky position-sticky d-flex align-items-center">
                                Selected Options
                                <span class="material-symbols-rounded selected-options-chevron">arrow_drop_down</span>
                            </span>
                            <span class="ms-auto d-none d-sm-inline fw-medium child-line-items-sum-total">${DollarFormat(item.childLineItems?.reduce((total, cli) => cli.subtotal.plus(total).toNumber(), 0) ?? 0)}</span>
                        </a>
                        <div class="collapse show" id="children-${item.id}">
                            <table class="table selected-options-table m-0">
                                <tbody class="w-100">
                                    ${item.childLineItems?.map(child => `<tr>
                                            <td class="c2 py-2 ps-10 text-start">${childLineItemName(child)}</td>
                                            <td class="c3 py-2 text-end">${Intl.NumberFormat().format(child.quantity)}</td>
                                            <td class="c4 py-2 text-end">${DollarFormat(child.pricePerUnit)}</td>
                                            <td class="c5 py-2 text-end">${DollarFormat(child.subtotal)}</td>
                                        </tr>`).join('') ?? noChildLineItemsPlaceholder}
                                </tbody>
                            </table>
                        </div>
                    </td>
                </tr>`;
        });

        const $table = html.FromString(`
            <div class="price-breakdown-container position-relative overflow-auto">
                <div class="w-100 h-100">
                    <table class="table price-breakdown rounded-0 mt-0 mx-auto overflow-auto">
                        <thead class="sticky-top z-3">
                            <tr>
                                <th class="c1 py-4 text-center position-sticky start-0">Item</th>
                                <th class="c2 py-4 text-start">Description</th>
                                <th class="c3 py-4 text-end">Quantity</th>
                                <th class="c4 py-4 text-end">Price per Unit</th>
                                <th class="c5 py-4 text-center">Subtotal</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${rows}
                        </tbody>
                        <tfoot class="sticky-bottom">
                            <tr class="">
                                <td colspan="3" class=""><!-- spacer ---></td>
                                <td colspan="2" class="c5 text-end py-5 px-4 position-sticky end-0"><b>Total</b> ${DollarFormat(opts.cartItem.subtotal ?? 0)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>`);

        return $table;
    };

    $card.querySelectorAll<HTMLButtonElement>('.price-breakdown-btn').forEach($btn => {
        $btn.addEventListener('click', () => showPriceBreakdown());
    });

    return $card;
}